// Phase 11d.5 — Cal.com-style demo booking widget
import PropTypes from "prop-types";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Video, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const TIME_SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM", "4:00 PM"];

function buildWeek() {
  const out = [];
  const base = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    out.push(d);
  }
  return out;
}

export function DemoBookingSheet({ open, onOpenChange }) {
  const days = buildWeek();
  const [activeIdx, setActiveIdx] = useState(0);
  const active = days[activeIdx];

  const book = (slot) => {
    const lbl = active.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    toast.success(`Demo booked for ${lbl} at ${slot} — check your inbox for confirmation`);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[480px] max-w-[100vw] p-0" data-testid="demo-booking-sheet">
        <SheetHeader className="px-5 py-4 border-b border-slate-100">
          <SheetTitle className="text-[14.5px] font-semibold flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"><Video size={13} /></span>
            <span>Book a demo with Sellyx</span>
          </SheetTitle>
          <SheetDescription className="sr-only">Pick a day and time to book a 30-minute call with the Sellyx founding team.</SheetDescription>
          <div className="text-[11.5px] text-slate-500 inline-flex items-center gap-1"><Clock size={11} /> 30 min · Zoom · Founding team</div>
        </SheetHeader>
        <div className="p-5 space-y-5 overflow-y-auto h-[calc(100vh-90px)] scrollbar-thin">
          {/* Day selector — 7 day horizontal */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Choose a day</div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-6 w-6" disabled><ChevronLeft size={12} /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" disabled><ChevronRight size={12} /></Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {days.map((d, i) => {
                const isActive = i === activeIdx;
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    className={cn(
                      "rounded-lg p-2 text-center transition-colors border",
                      isActive ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200" : "border-slate-200 bg-white hover:border-slate-300",
                    )}
                    data-testid={`demo-day-${i}`}
                  >
                    <div className={cn("text-[10px] uppercase tracking-wider font-semibold", isActive ? "text-indigo-700" : "text-slate-400")}>{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                    <div className={cn("mt-0.5 text-[14px] font-semibold tabular-nums", isActive ? "text-slate-900" : "text-slate-700")}>{d.getDate()}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time slots — 6 per day */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Available times — {active.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
            <div className="grid grid-cols-2 gap-2">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => book(slot)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors text-left"
                  data-testid={`demo-slot-${slot}`}
                >
                  <div className="text-[13px] font-semibold text-slate-900 tabular-nums">{slot}</div>
                  <div className="text-[10.5px] text-slate-500">30 min</div>
                </button>
              ))}
            </div>
          </div>

          <div className="text-center text-[10.5px] text-slate-400 pt-3 border-t border-slate-100">
            Powered by <span className="font-mono text-slate-600">Cal.com</span>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

DemoBookingSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};
